import { Button, Typography } from '@smartgift/frontend-toolkit';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { OrderStatusBadge } from '@hero/components/molecules';
import {
  CONTACTLESS_VERIFICATION_TYPES,
  RECOGNITIONS_TAB,
  RECOGNITION_STATUSES
} from '@hero/constants';
import { ShareAnywhere, SharePublicLink } from '@hero/organisms';
import { ROUTE_PATHS } from '@hero/routes';
import { useOnboardingStore } from '@hero/store/hooks';
import { formatDate } from '@hero/utils';

import * as S from './style';

const recipientVerificationTranslationMapping = {
  [CONTACTLESS_VERIFICATION_TYPES.PHONE]: 'phoneVerificationRequired',
  [CONTACTLESS_VERIFICATION_TYPES.EMAIL_DOMAIN]: 'authorizeDomainOnly',
  [CONTACTLESS_VERIFICATION_TYPES.NONE]: 'noVerificationRequiredLive'
};

function NewShareTemplate({
  recipientCount,
  secureSingleLink,
  featureType,
  recognitionCode,
  status,
  name,
  endDate,
  recipientVerificationType
}) {
  const { t } = useTranslation();
  const onboarding = useOnboardingStore();
  const navigate = useNavigate();

  return (
    <S.ShareWrapper>
      <S.ShareLeft>
        <S.ShareHeader>
          <Typography variant="body1Bold">{t('status')}</Typography>
          <OrderStatusBadge status={status || RECOGNITION_STATUSES.live} />
        </S.ShareHeader>
        <S.ShareBody>
          <S.ShareSection hasBorderBottom={!!recognitionCode}>
            <SharePublicLink
              isInfoActive={onboarding?.[featureType]?.infoCardsActive}
              secureSingleLink={secureSingleLink}
              featureType={featureType}
              recipientCount={recipientCount}
            />
            <Typography variant="body2">{t('embedGiftInEmailMessagesDigital')}</Typography>
          </S.ShareSection>
          {recognitionCode && (
            <>
              <S.ShareSection hasBorderBottom>
                <Typography variant="body1Bold">{t('orderSummary')}</Typography>
                <S.ShareTableRow>
                  <Typography variant="body2SemiBold">{t('giftCode') + ':'}</Typography>
                  <Typography variant="body2Bold">{recognitionCode}</Typography>
                </S.ShareTableRow>
                <S.ShareTableRow>
                  <Typography variant="body2SemiBold">{t('endDate') + ':'}</Typography>
                  <Typography variant="body2Bold">
                    {formatDate(endDate, { includeZone: true })}
                  </Typography>
                </S.ShareTableRow>
                <S.ShareTableRow>
                  <Typography variant="body2SemiBold">{t('name') + ':'}</Typography>
                  <Typography variant="body2Bold">{name}</Typography>
                </S.ShareTableRow>
                <S.ShareTableRow>
                  <Typography variant="body2SemiBold">
                    {t('recipients') + ':'}
                    <Typography variant="caption1" color="neutral" emphasis="400">
                      (
                      {t(
                        recipientVerificationTranslationMapping[recipientVerificationType]
                      ).toLowerCase()}
                      )
                    </Typography>
                  </Typography>
                  <Typography variant="body2Bold">{recipientCount}</Typography>
                </S.ShareTableRow>
              </S.ShareSection>
              <S.ShareSection>
                <Typography variant="body2">
                  <Trans
                    i18nKey="orderConfirmedInfoText"
                    components={{
                      a: (
                        <S.SupportLink
                          href={`mailto:${t('mailTo')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        />
                      )
                    }}
                  />
                </Typography>

                <S.ButtonGroup>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      navigate(ROUTE_PATHS.MY_RECOGNITION_DETAIL(recognitionCode));
                    }}
                  >
                    {t('seeDetails')}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      navigate(
                        `${ROUTE_PATHS.MY_RECOGNITION_DETAIL(recognitionCode)}#${
                          RECOGNITIONS_TAB.analytics
                        }`
                      );
                    }}
                  >
                    {t('seeAnalytics')}
                  </Button>
                </S.ButtonGroup>
              </S.ShareSection>
            </>
          )}
        </S.ShareBody>
      </S.ShareLeft>

      <ShareAnywhere secureSingleLink={secureSingleLink} recognitionCode={recognitionCode} />
    </S.ShareWrapper>
  );
}

export { NewShareTemplate };
